import { IconEditOrders } from '../../../../../../../assets/DesignSystem/SVGComponents';
import { IShipmentData, SHIPMENT_STATUS, SHIPMENT_STATUS_ID } from '../../../../../../../types/orders';
import { formatShortDate } from '../../../../../../../utils/formatDatesAndHours';
import './ShipmentTrackingData.scss';

export type ShipmentTrackingStepKey = 'deliveryProcess' | 'transitToDestination' | 'transitToWarehouse';

interface IShipmentTrackingDataProps {
  shipmentData: IShipmentData;
  countryTimeZone?: number;
  setShowProcessModal: React.Dispatch<React.SetStateAction<boolean>>;
  setIsEditedProcessModal: React.Dispatch<React.SetStateAction<boolean>>;
  trackingStepKey?: ShipmentTrackingStepKey;
  onEditInboundTransitClick?: () => void;
}

const isValidHttpUrl = (url: string | null | undefined): url is string =>
  !!url && (url.includes('http://') || url.includes('https://'));

const ShipmentTrackingData = ({
  shipmentData,
  countryTimeZone,
  setShowProcessModal,
  setIsEditedProcessModal,
  trackingStepKey,
  onEditInboundTransitClick
}: IShipmentTrackingDataProps) => {
  const { trackingInfo, status, warehouseTemporaryStorage } = shipmentData || {};
  const { trackingLink, trackerNumber, currentEstimatedDeliverDate, oldEstimatedDeliveryDates } = trackingInfo || {};
  const wts = warehouseTemporaryStorage;
  const wtsOldDates = wts?.oldEstimatedEntryDates ?? [];

  const isTransitToWarehouseStep = trackingStepKey === 'transitToWarehouse';

  const currentEstimatedDate = isTransitToWarehouseStep
    ? wts?.estimatedEntryDate?.trim() || undefined
    : currentEstimatedDeliverDate || undefined;

  const oldDatesList = isTransitToWarehouseStep ? wtsOldDates : (oldEstimatedDeliveryDates ?? []);

  const displayTrackingUrl = isTransitToWarehouseStep
    ? (wts?.trackingUrl?.trim() || undefined)
    : trackingLink || undefined;

  const displayTrackingCode = isTransitToWarehouseStep
    ? (wts?.trackingCode?.trim() || (trackerNumber != null ? String(trackerNumber).trim() : '') || undefined)
    : trackerNumber != null
      ? String(trackerNumber)
      : undefined;

  const shipmentWasDelivered = status === SHIPMENT_STATUS.DELIVERED;
  const shipmentWasCancelled = status === SHIPMENT_STATUS.CANCELLED;
  const shipmentIsTransitToWarehouse = shipmentData?.statusId === SHIPMENT_STATUS_ID.TRANSIT_TO_WAREHOUSE;
  const shipmentIsOnCourse = !shipmentWasDelivered && !shipmentWasCancelled;
  const noDataText = 'Sin datos';

  const activeStyles = (() => {
    return status === SHIPMENT_STATUS.DELIVERY_PROCESS ? 'active' : '';
  })();

  const trackingLinkIsValid = isValidHttpUrl(displayTrackingUrl);
  const linkStyles = trackingLinkIsValid ? 'link' : '';

  const openTrackingUrl = () => {
    if (trackingLinkIsValid && displayTrackingUrl) {
      window.open(displayTrackingUrl, '_blank');
    }
  };

  const handleEditEstimatedDates = () => {
    if (!shipmentIsOnCourse) return;
    if (isTransitToWarehouseStep) {
      onEditInboundTransitClick?.();
      return;
    }
    setShowProcessModal(true);
    setIsEditedProcessModal(true);
  };

  const hasOldEstimatedRows = oldDatesList.some(d => !!d?.trim());
  const showEstimatedSection = isTransitToWarehouseStep
    ? !!currentEstimatedDate || hasOldEstimatedRows
    : !!currentEstimatedDate;

  const showEditEstimatedIcon = isTransitToWarehouseStep
    ? shipmentIsTransitToWarehouse && !!onEditInboundTransitClick
    : shipmentIsOnCourse;

  const estimatedLabel = 'Fecha limite estimada:';

  const showEstimatedHeaderRow =
    !!currentEstimatedDate ||
    (isTransitToWarehouseStep && hasOldEstimatedRows && showEditEstimatedIcon);

  return (
    <div className={`shipmentTrackingData ${activeStyles}`}>
      {showEstimatedSection && (
        <div className="estimatedDatesSection">
          {showEstimatedHeaderRow && (
            <div className="trackingDataField estimatedDate">
              {estimatedLabel}{' '}
              {currentEstimatedDate
                ? formatShortDate(currentEstimatedDate, countryTimeZone)?.textWithTimezone
                : noDataText}
              {showEditEstimatedIcon && (
                <IconEditOrders onClick={handleEditEstimatedDates} className="marginIcon" />
              )}
            </div>
          )}
          <div className="oldDatesBlock">
            {oldDatesList?.length > 0 &&
              oldDatesList.map((item, index) => {
                if (!item?.trim()) return null;
                return (
                  <div className="oldDate" key={`old-estimated-${trackingStepKey ?? 'default'}-${item}-i=${index}`}>
                    {formatShortDate(item)?.textWithTimezone}
                  </div>
                );
              })}
          </div>
        </div>
      )}
      {displayTrackingUrl && (
        <div className="trackingDataField trackingLinkField">
          Link de rastreo:{' '}
          <div className={linkStyles} onClick={openTrackingUrl}>
            {displayTrackingUrl || noDataText}
          </div>
        </div>
      )}
      {!!displayTrackingCode && (
        <div className="trackingDataField">Código: {displayTrackingCode || noDataText}</div>
      )}
    </div>
  );
};

export default ShipmentTrackingData;
